import React, { useState, useEffect, useRef } from 'react';
import { 
  Edit3, 
  MessageCircle, 
  Star, 
  Calendar,
  MapPin,
  Phone,
  Clock,
  Save,
  X,
  Plus,
  Camera,
  Eye,
  Reply,
  Trash2,
  Settings,
  CheckCircle,
  UserPlus,
  Share2,
  Copy
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import axios from 'axios';
import { BARBER_AVATARS, getBarbershopAvatarUrl } from '../../lib/avatars';
import { formatDateWithTZ } from '../../lib/utils';
import { useGooglePlaces } from '../../hooks/useGooglePlaces';
import { BarbershopMapSheet } from '../app/BarbershopMapSheet';

export const BarbershopManagement = ({ barbershop, onUpdate, BACKEND_URL, onNotify, googleMapsApiKey }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [showMapSheet, setShowMapSheet] = useState(false);
  const [reviews, setReviews] = useState([]);
  const reviewsRef = useRef([]);
  const reviewsHashRef = useRef('');
  const profileImageInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const portfolioInputRef = useRef(null);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [addressError, setAddressError] = useState('');
  const { isLoaded, inputRef, setupPlaceChangedListener } = useGooglePlaces(googleMapsApiKey);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const initialAddressRef = useRef(barbershop?.address || '');
  const initialLatRef = useRef(barbershop?.lat);
  const initialLngRef = useRef(barbershop?.lng);

  const notify = (title, description, variant = 'info') => {
    if (onNotify) {
      onNotify(title, description, variant);
      return;
    }
    console.error(title, description);
  };
  
  const [formData, setFormData] = useState({
    name: barbershop?.name || '',
    description: barbershop?.description || '',
    address: barbershop?.address || '',
    city: barbershop?.city || '',
    comuna: barbershop?.comuna || '',
    region: barbershop?.region || '',
    street: barbershop?.street || '',
    street_number: barbershop?.street_number || '',
    postal_code: barbershop?.postal_code || '',
    lat: typeof barbershop?.lat === 'number' ? barbershop.lat : null,
    lng: typeof barbershop?.lng === 'number' ? barbershop.lng : null,
    phone: barbershop?.phone || '',
    services: barbershop?.services || [],
    portfolio_images: barbershop?.portfolio_images || [],
    cover_image: barbershop?.cover_image || '',
    working_hours: barbershop?.working_hours || {},
    accepting_bookings: barbershop?.accepting_bookings !== false,
    accepting_quick_cuts: barbershop?.accepting_quick_cuts !== false,
    solo_agenda: barbershop?.solo_agenda === true
  });

  useEffect(() => {
    if (!barbershop?.id) return;
    initialAddressRef.current = barbershop.address || '';
    initialLatRef.current = barbershop.lat;
    initialLngRef.current = barbershop.lng;
    setFormData({
      name: barbershop?.name || '',
      description: barbershop?.description || '',
      address: barbershop?.address || '',
      city: barbershop?.city || '',
      comuna: barbershop?.comuna || '',
      region: barbershop?.region || '',
      street: barbershop?.street || '',
      street_number: barbershop?.street_number || '',
      postal_code: barbershop?.postal_code || '',
      lat: typeof barbershop?.lat === 'number' ? barbershop.lat : null,
      lng: typeof barbershop?.lng === 'number' ? barbershop.lng : null,
      phone: barbershop?.phone || '',
      services: barbershop?.services || [],
      portfolio_images: barbershop?.portfolio_images || [],
      cover_image: barbershop?.cover_image || '',
      working_hours: barbershop?.working_hours || {},
      accepting_bookings: barbershop?.accepting_bookings !== false,
      accepting_quick_cuts: barbershop?.accepting_quick_cuts !== false,
      solo_agenda: barbershop?.solo_agenda === true
    });
    setSelectedPlace(null);
    setAddressError('');
  }, [barbershop?.id]);

  useEffect(() => {
    if (!isLoaded) return;
    setupPlaceChangedListener((place) => {
      const components = place.components || {};
      setSelectedPlace(place);
      setAddressError('');
      setFormData((prev) => ({
        ...prev,
        address: place.formatted_address,
        lat: place.lat,
        lng: place.lng,
        city: components.city || '',
        comuna: components.comuna || components.city || '',
        region: components.region || components.state || '',
        street: components.street_name || '',
        street_number: components.street_number || '',
        postal_code: components.postal_code || ''
      }));
    });
  }, [isLoaded, setupPlaceChangedListener]);

  // Load reviews and gallery
  useEffect(() => {
    if (barbershop?.id) {
      loadReviews();
    }
  }, [barbershop?.id]);

  useEffect(() => {
    if (!barbershop?.id || !showReviews) return;
    const reviewsInterval = setInterval(() => {
      loadReviews();
    }, 15000);
    return () => clearInterval(reviewsInterval);
  }, [barbershop?.id, showReviews]);

  const normalizeReviewResponse = (value) => {
    if (!value) return '';
    return typeof value === 'string' ? value : JSON.stringify(value);
  };

  const sortReviews = (list) => {
    return [...list].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      if (dateA !== dateB) return dateB - dateA;
      const idA = a.id || 0;
      const idB = b.id || 0;
      return idB - idA;
    });
  };

  const buildReviewsHash = (list) => {
    return list
      .map((review) => [
        review.id,
        review.rating,
        review.comment,
        review.published,
        review.hidden,
        normalizeReviewResponse(review.barber_response),
        review.created_at,
        review.updated_at,
        review.client_name
      ].join('|'))
      .join('::');
  };

  const loadReviews = async () => {
    try {
      if (!barbershop?.id) {
        console.log('No barbershop ID available');
        setReviews([]);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setReviews([]);
        return;
      }

      if (!BACKEND_URL) {
        console.error('BACKEND_URL no está definido');
        setReviews([]);
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/reviews/barbershop/${barbershop.id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      const newReviews = sortReviews(response.data.reviews || []);
      const newHash = buildReviewsHash(newReviews);
      const currentHash = reviewsHashRef.current;
      if (newHash === currentHash) {
        return;
      }
      
      // Comparar usando ref para evitar re-renders innecesarios
      const currentReviews = reviewsRef.current || [];
      const hasChanged = 
        newReviews.length !== currentReviews.length ||
        newReviews.some((newReview, index) => {
          const oldReview = currentReviews[index];
          return !oldReview || 
                 newReview.id !== oldReview.id ||
                 newReview.rating !== oldReview.rating ||
                 newReview.comment !== oldReview.comment ||
                 newReview.published !== oldReview.published ||
                 newReview.hidden !== oldReview.hidden ||
                 normalizeReviewResponse(newReview.barber_response) !== normalizeReviewResponse(oldReview.barber_response);
        });
      
      // Solo actualizar el estado si hay cambios reales (evitar pestañeo)
      if (hasChanged) {
        reviewsRef.current = newReviews;
        reviewsHashRef.current = newHash;
        setReviews(newReviews);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      // Solo actualizar si hay un error y el estado actual no está vacío
      if (reviews.length > 0) {
        // No actualizar a vacío si ya hay reseñas, para evitar pestañeo
      }
    }
  };

  const handleSaveChanges = async () => {
    try {
      const addressUnchanged = formData.address === initialAddressRef.current;
      const hasCoords = Number.isFinite(formData.lat) && Number.isFinite(formData.lng);
      const hasInitialCoords = Number.isFinite(initialLatRef.current) && Number.isFinite(initialLngRef.current);
      const addressValid = !!selectedPlace || (addressUnchanged && hasInitialCoords);
      if (!addressValid) {
        setAddressError('Debes seleccionar una dirección válida desde Google.');
        notify('Dirección inválida', 'Selecciona una dirección real desde Google.', 'destructive');
        return;
      }
      setAddressError('');
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BACKEND_URL}/api/barbershops/${barbershop.id}`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setIsEditing(false);
      onUpdate(response.data);
      
    } catch (error) {
      console.error('Error updating barbershop:', error);
    }
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notify('Archivo inválido', 'El archivo debe ser una imagen.', 'destructive');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notify('Archivo inválido', 'La imagen debe ser menor a 5MB.', 'destructive');
      return;
    }

    try {
      setUploadingProfileImage(true);
      const token = localStorage.getItem('token');
      let imageUrl = null;

      try {
        const authRes = await axios.get(`${BACKEND_URL}/api/imagekit/auth`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const uploadData = new FormData();
        uploadData.append('file', file);
        uploadData.append('fileName', `${Date.now()}-${file.name}`);
        uploadData.append('publicKey', authRes.data.publicKey);
        uploadData.append('signature', authRes.data.signature);
        uploadData.append('expire', authRes.data.expire);
        uploadData.append('token', authRes.data.token);
        uploadData.append('folder', '/barbershops');

        const uploadRes = await axios.post('https://upload.imagekit.io/api/v1/files/upload', uploadData);
        imageUrl = uploadRes.data.url;
      } catch (imageKitError) {
        console.warn('ImageKit error:', imageKitError);
      }

      if (!imageUrl) {
        notify('Error al subir', 'No se pudo subir la imagen. Revisa ImageKit.', 'destructive');
        return;
      }

      const response = await axios.put(
        `${BACKEND_URL}/api/barbershops/${barbershop.id}`,
        { profile_image: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onUpdate(response.data);
    } catch (error) {
      console.error('Error uploading profile image:', error);
      notify('Error al subir', 'No se pudo subir la foto. Inténtalo nuevamente.', 'destructive');
    } finally {
      setUploadingProfileImage(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleCoverImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notify('Archivo inválido', 'El archivo debe ser una imagen.', 'destructive');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notify('Archivo inválido', 'La imagen debe ser menor a 5MB.', 'destructive');
      return;
    }

    try {
      setUploadingCoverImage(true);
      const token = localStorage.getItem('token');
      let imageUrl = null;

      try {
        const authRes = await axios.get(`${BACKEND_URL}/api/imagekit/auth`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const uploadData = new FormData();
        uploadData.append('file', file);
        uploadData.append('fileName', `${Date.now()}-${file.name}`);
        uploadData.append('publicKey', authRes.data.publicKey);
        uploadData.append('signature', authRes.data.signature);
        uploadData.append('expire', authRes.data.expire);
        uploadData.append('token', authRes.data.token);
        uploadData.append('folder', '/barbershops/covers');

        const uploadRes = await axios.post('https://upload.imagekit.io/api/v1/files/upload', uploadData);
        imageUrl = uploadRes.data.url;
      } catch (imageKitError) {
        console.warn('ImageKit error:', imageKitError);
      }

      if (!imageUrl) {
        notify('Error al subir', 'No se pudo subir la imagen. Revisa ImageKit.', 'destructive');
        return;
      }

      const response = await axios.put(
        `${BACKEND_URL}/api/barbershops/${barbershop.id}`,
        { cover_image: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData((prev) => ({ ...prev, cover_image: imageUrl }));
      onUpdate(response.data);
    } catch (error) {
      console.error('Error uploading cover image:', error);
      notify('Error al subir', 'No se pudo subir la portada. Inténtalo nuevamente.', 'destructive');
    } finally {
      setUploadingCoverImage(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handlePortfolioUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const invalid = files.find((file) => !file.type.startsWith('image/'));
    if (invalid) {
      notify('Archivo inválido', 'Solo puedes subir imágenes.', 'destructive');
      return;
    }
    if (files.some((file) => file.size > 5 * 1024 * 1024)) {
      notify('Archivo inválido', 'Cada imagen debe ser menor a 5MB.', 'destructive');
      return;
    }
    try {
      setUploadingPortfolio(true);
      const token = localStorage.getItem('token');
      let uploadedUrls = [];
      try {
        const authRes = await axios.get(`${BACKEND_URL}/api/imagekit/auth`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        uploadedUrls = await Promise.all(
          files.map(async (file) => {
            const uploadData = new FormData();
            uploadData.append('file', file);
            uploadData.append('fileName', `${Date.now()}-${file.name}`);
            uploadData.append('publicKey', authRes.data.publicKey);
            uploadData.append('signature', authRes.data.signature);
            uploadData.append('expire', authRes.data.expire);
            uploadData.append('token', authRes.data.token);
            uploadData.append('folder', '/portfolio');
            const uploadRes = await axios.post('https://upload.imagekit.io/api/v1/files/upload', uploadData);
            return uploadRes.data.url;
          })
        );
      } catch (imageKitError) {
        console.warn('ImageKit error:', imageKitError);
      }
      if (uploadedUrls.length === 0) {
        notify('Error al subir', 'No se pudo subir el portafolio. Revisa ImageKit.', 'destructive');
        return;
      }
      const nextImages = [...(formData.portfolio_images || []), ...uploadedUrls];
      const response = await axios.put(
        `${BACKEND_URL}/api/barbershops/${barbershop.id}`,
        { portfolio_images: nextImages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData((prev) => ({ ...prev, portfolio_images: nextImages }));
      onUpdate(response.data);
      notify('Portafolio actualizado', 'Imágenes agregadas correctamente.', 'success');
    } catch (error) {
      console.error('Error uploading portfolio:', error);
      notify('Error al subir', 'No se pudieron subir las imágenes.', 'destructive');
    } finally {
      setUploadingPortfolio(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleRemovePortfolioImage = async (imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      const nextImages = (formData.portfolio_images || []).filter((img) => img !== imageUrl);
      const response = await axios.put(
        `${BACKEND_URL}/api/barbershops/${barbershop.id}`,
        { portfolio_images: nextImages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData((prev) => ({ ...prev, portfolio_images: nextImages }));
      onUpdate(response.data);
      notify('Portafolio actualizado', 'Imagen eliminada.', 'success');
    } catch (error) {
      console.error('Error removing portfolio image:', error);
      notify('Error', 'No se pudo eliminar la imagen.', 'destructive');
    }
  };

  const addService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { name: '', price: 0, duration: 30 }]
    });
  };

  const updateService = (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index] = { ...newServices[index], [field]: value };
    setFormData({ ...formData, services: newServices });
  };

  const removeService = (index) => {
    const newServices = formData.services.filter((_, i) => i !== index);
    setFormData({ ...formData, services: newServices });
  };

  const respondToReview = async (reviewId, response) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Token encontrado:', token ? 'Sí' : 'No');
      console.log('BACKEND_URL:', BACKEND_URL);
      console.log('Review ID:', reviewId);
      console.log('Response text:', response);
      
      if (!token) {
        console.error('No authentication token found in localStorage');
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      if (!BACKEND_URL) {
        console.error('BACKEND_URL no está definido');
        notify('Configuración', 'URL del backend no encontrada.', 'destructive');
        return;
      }

      if (!reviewId) {
        console.error('Review ID no válido');
        notify('Error', 'ID de reseña no válido.', 'destructive');
        return;
      }

      if (!response || !response.trim()) {
        notify('Atención', 'Escribe una respuesta antes de enviar.', 'info');
        return;
      }
      
      console.log('Enviando respuesta a:', `${BACKEND_URL}/api/reviews/${reviewId}/respond`);
      
      const result = await axios.post(
        `${BACKEND_URL}/api/reviews/${reviewId}/respond`,
        { response: response.trim() },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Respuesta enviada exitosamente:', result.data);
      notify('Respuesta enviada', 'Tu respuesta se envió correctamente.', 'success');
      loadReviews(); // Refresh reviews
      
    } catch (error) {
      console.error('Error completo al responder reseña:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      if (error.response?.status === 401) {
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
      } else if (error.response?.status === 403) {
        notify('Sin permisos', 'No puedes responder esta reseña.', 'destructive');
      } else if (error.response?.status === 404) {
        notify('No encontrada', 'Reseña no encontrada.', 'destructive');
      } else if (error.response?.data?.detail) {
        notify('Error', error.response.data.detail, 'destructive');
      } else {
        notify('Error', error.message || 'Error desconocido', 'destructive');
      }
    }
  };

  const publishReview = async (reviewId, published) => {
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      if (!BACKEND_URL) {
        notify('Configuración', 'URL del backend no encontrada.', 'destructive');
        return;
      }

      console.log('Publicando reseña:', reviewId, 'published:', published);
      
      // Verificar si el token es válido antes de hacer la petición
      try {
        const verifyResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('Token válido, procediendo con la publicación');
      } catch (verifyError) {
        console.error('Token inválido o expirado:', verifyError);
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        // Opcional: redirigir al login
        return;
      }
      
      const response = await axios.put(`${BACKEND_URL}/api/reviews/${reviewId}/publish`, {
        published: published
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      notify('Reseña actualizada', published ? 'Reseña publicada.' : 'Reseña despublicada.', 'success');
      loadReviews();
      
    } catch (error) {
      console.error('Error publishing review:', error);
      console.error('Error response:', error.response);
      if (error.response?.status === 401) {
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        // Opcional: limpiar token y redirigir
        localStorage.removeItem('token');
      } else {
        notify('Error', error.response?.data?.detail || error.message, 'destructive');
      }
    }
  };

  const hideReview = async (reviewId, hidden) => {
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      if (!BACKEND_URL) {
        notify('Configuración', 'URL del backend no encontrada.', 'destructive');
        return;
      }

      console.log('Ocultando reseña:', reviewId, 'hidden:', hidden);
      
      // Verificar si el token es válido antes de hacer la petición
      try {
        const verifyResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('Token válido, procediendo con ocultar');
      } catch (verifyError) {
        console.error('Token inválido o expirado:', verifyError);
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }
      
      const response = await axios.put(`${BACKEND_URL}/api/reviews/${reviewId}/publish`, {
        hidden: hidden
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      notify('Reseña actualizada', hidden ? 'Reseña ocultada.' : 'Reseña mostrada.', 'success');
      loadReviews();
      
    } catch (error) {
      console.error('Error hiding review:', error);
      console.error('Error response:', error.response);
      if (error.response?.status === 401) {
        notify('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        localStorage.removeItem('token');
      } else {
        notify('Error', error.response?.data?.detail || error.message, 'destructive');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          onClick={() => setIsEditing(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Edit3 className="w-4 h-4 mr-2" />
          Editar Información
        </Button>
        
        <Button
          onClick={() => setShowReviews(true)}
          className="bg-amber-600 hover:bg-amber-700"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          Ver Reseñas ({reviews.length})
        </Button>

        {barbershop?.id && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-gray-600 text-gray-200 hover:bg-gray-800">
                <UserPlus className="w-4 h-4 mr-2" />
                Invitar barberos
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64">
              <div className="px-2 py-2 text-xs uppercase text-gray-500 border-b border-gray-700">Compartir enlace de invitación</div>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  const url = `${typeof window !== 'undefined' ? window.location.origin : ''}${typeof window !== 'undefined' ? (window.location.pathname || '/') : '/'}?join_barbershop=${barbershop.id}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(`Únete a ${barbershop.name} en CÓRTATE.CL: ${url}`)}`, '_blank');
                }}
              >
                <Share2 className="w-4 h-4 mr-2" /> Enviar por WhatsApp
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  const url = `${typeof window !== 'undefined' ? window.location.origin : ''}${typeof window !== 'undefined' ? (window.location.pathname || '/') : '/'}?join_barbershop=${barbershop.id}`;
                  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                }}
              >
                <Share2 className="w-4 h-4 mr-2" /> Compartir en LinkedIn
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  const url = `${typeof window !== 'undefined' ? window.location.origin : ''}${typeof window !== 'undefined' ? (window.location.pathname || '/') : '/'}?join_barbershop=${barbershop.id}`;
                  navigator.clipboard?.writeText(url).then(() => notify('Enlace copiado', 'Pega el enlace para invitar a un barbero.', 'success')).catch(() => notify('Error', 'No se pudo copiar', 'destructive'));
                }}
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar enlace
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Barbershop Profile Header */}
      <Card className="bg-gray-900 border-gray-700 overflow-hidden">
        <div className="relative">
          <div className="h-40 md:h-56 bg-gray-800">
            {barbershop?.cover_image ? (
              <img
                src={barbershop.cover_image}
                alt="Portada barbería"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                Agrega una portada profesional
              </div>
            )}
          </div>
          <div className="absolute -bottom-6 left-6 flex items-end gap-4">
            <img
              src={getBarbershopAvatarUrl(barbershop)}
              alt="Perfil barbería"
              loading="lazy"
              className="w-20 h-20 rounded-full border-4 border-gray-900 object-cover"
            />
            <div className="pb-2">
              <h3 className="text-white text-xl font-semibold">{barbershop?.name}</h3>
              <p className="text-gray-400 text-sm">{barbershop?.address || 'Dirección por definir'}</p>
            </div>
          </div>
        </div>
        <CardContent className="pt-10 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-400 text-sm mb-2">Descripción</p>
              <p className="text-gray-200 text-sm">{barbershop?.description || 'Cuéntales a tus clientes sobre tu estilo.'}</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <MapPin className="w-4 h-4" />
                {barbershop?.address || 'Dirección por definir'}
                {barbershop?.address && (
                  <button
                    type="button"
                    onClick={() => setShowMapSheet(true)}
                    className="ml-2 text-xs text-amber-300 hover:text-amber-200"
                  >
                    Ver mapa
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Phone className="w-4 h-4" />
                {barbershop?.phone || 'Teléfono por definir'}
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Star className="w-4 h-4" />
                {barbershop?.rating || 0}/5 ({reviews.length} reseñas)
              </div>
              {barbershop?.weekly_rank && (
                <div className="flex items-center gap-2 text-amber-300">
                  <Star className="w-4 h-4" />
                  Top semanal #{barbershop.weekly_rank}
                </div>
              )}
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="text-white font-medium mb-2">Servicios</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {barbershop?.services?.map((service, index) => (
                <div key={index} className="bg-gray-800 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-white text-sm">{service.name}</span>
                    <div className="text-right">
                      <span className="text-amber-400 font-medium">${service.price?.toLocaleString()}</span>
                      <div className="text-gray-400 text-xs">{service.duration} min</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gestión de Reseñas */}
          <div className="mt-6">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-amber-400" />
              Gestión de Reseñas
            </h4>
            <div className="bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-white font-medium">{reviews.length} Reseña{reviews.length !== 1 ? 's' : ''}</p>
                  <p className="text-gray-400 text-sm">Calificación promedio: {barbershop?.rating || 0}/5</p>
                </div>
                <Button
                  onClick={() => setShowReviews(true)}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Ver Todas
                </Button>
              </div>
              
              <div className="text-center py-4">
                {reviews.length > 0 ? (
                  <p className="text-gray-400 text-sm">
                    Las reseñas están agrupadas en “Ver Todas”.
                  </p>
                ) : (
                  <>
                    <p className="text-gray-400 text-sm">No hay reseñas aún</p>
                    <p className="text-gray-500 text-xs mt-1">Las reseñas de tus clientes aparecerán aquí</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <BarbershopMapSheet
        open={showMapSheet}
        onOpenChange={setShowMapSheet}
        apiKey={googleMapsApiKey}
        barbershops={barbershop ? [barbershop] : []}
        getBarbershopAvatarUrl={getBarbershopAvatarUrl}
        onReserve={() => {}}
        onViewProfile={() => {}}
        focusBarbershop={barbershop || null}
      />

      {/* Edit Information Modal */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Información de la Barbería</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="space-y-3">
              <label className="text-white text-sm font-medium block">Portada y foto de perfil</label>
              <div className="rounded-lg border border-gray-800 bg-gray-900/60 overflow-hidden">
                <div className="relative h-32 md:h-44 bg-gray-800">
                  {formData.cover_image ? (
                    <img src={formData.cover_image} alt="Portada barbería" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                      Agrega una portada profesional
                    </div>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="absolute top-2 right-2 border-gray-700 text-gray-200 bg-gray-900/70"
                    onClick={() => coverImageInputRef.current?.click()}
                    disabled={uploadingCoverImage}
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    {uploadingCoverImage ? 'Subiendo...' : 'Cambiar portada'}
                  </Button>
                  <input
                    ref={coverImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex items-center gap-4 p-4">
                  <img
                    src={getBarbershopAvatarUrl(barbershop)}
                    alt="Perfil barbería"
                    className="w-16 h-16 rounded-full border-2 border-gray-700 object-cover"
                  />
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700"
                      onClick={() => profileImageInputRef.current?.click()}
                      disabled={uploadingProfileImage}
                    >
                      {uploadingProfileImage ? 'Subiendo...' : 'Cambiar foto perfil'}
                    </Button>
                    <input
                      ref={profileImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImageUpload}
                      className="hidden"
                    />
                    <p className="text-xs text-gray-400">Portada y perfil en JPG/PNG hasta 5MB.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-gray-700 text-gray-200">
                      Elegir avatar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {BARBER_AVATARS.map((avatarUrl, index) => (
                      <DropdownMenuItem
                        key={avatarUrl}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const response = await axios.put(
                              `${BACKEND_URL}/api/barbershops/${barbershop.id}`,
                              { profile_image: avatarUrl },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            onUpdate(response.data);
                          } catch (error) {
                            console.error('Error updating barbershop avatar:', error);
                          }
                        }}
                        className="cursor-pointer text-white"
                      >
                        <div className="flex items-center gap-2">
                          <img src={avatarUrl} alt={`Avatar ${index + 1}`} loading="lazy" decoding="async" className="w-6 h-6 rounded-full object-cover" />
                          <span>Avatar {index + 1}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <p className="text-xs text-gray-400">Puedes elegir un avatar o subir una foto.</p>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Nombre</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Teléfono</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>

            <div>
              <label className="text-white text-sm font-medium mb-2 block">Dirección</label>
              <Input
                ref={inputRef}
                value={formData.address}
                onChange={(e) => {
                  setFormData({...formData, address: e.target.value});
                  setSelectedPlace(null);
                  setAddressError('');
                }}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                autoComplete="off"
              />
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                {selectedPlace || (formData.address === initialAddressRef.current && Number.isFinite(formData.lat) && Number.isFinite(formData.lng)) ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Dirección validada</span>
                  </>
                ) : (
                  <span>Selecciona una dirección real desde la lista.</span>
                )}
              </div>
              {addressError && (
                <p className="text-xs text-red-400 mt-1">{addressError}</p>
              )}
            </div>

            <div>
              <label className="text-white text-sm font-medium mb-2 block">Descripción</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                rows={3}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
                <div>
                  <p className="text-white text-sm font-medium">Aceptar reservas</p>
                  <p className="text-xs text-gray-400">Reservas tradicionales</p>
                </div>
                <Switch
                  checked={formData.accepting_bookings}
                  onCheckedChange={(value) => setFormData({ ...formData, accepting_bookings: value })}
                />
              </div>
              <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
                <div>
                  <p className="text-white text-sm font-medium">Aceptar cortes rápidos</p>
                  <p className="text-xs text-gray-400">Solicitudes inmediatas</p>
                </div>
                <Switch
                  checked={formData.accepting_quick_cuts}
                  onCheckedChange={(value) => setFormData({ ...formData, accepting_quick_cuts: value })}
                  disabled={formData.solo_agenda}
                />
              </div>
              <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 md:col-span-2">
                <div>
                  <p className="text-white text-sm font-medium">Modo “Solo agenda”</p>
                  <p className="text-xs text-gray-400">Desactiva cortes rápidos y acepta solo reservas.</p>
                </div>
                <Switch
                  checked={formData.solo_agenda}
                  onCheckedChange={(value) => setFormData({
                    ...formData,
                    solo_agenda: value,
                    accepting_quick_cuts: value ? false : formData.accepting_quick_cuts
                  })}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-white font-medium">Portafolio de trabajos</label>
                <Button
                  onClick={() => portfolioInputRef.current?.click()}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={uploadingPortfolio}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {uploadingPortfolio ? 'Subiendo...' : 'Agregar fotos'}
                </Button>
                <input
                  ref={portfolioInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePortfolioUpload}
                  className="hidden"
                />
              </div>
              {(formData.portfolio_images || []).length === 0 ? (
                <div className="text-xs text-gray-500">Aún no has agregado imágenes.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {formData.portfolio_images.map((imageUrl) => (
                    <div key={imageUrl} className="relative group rounded-lg overflow-hidden border border-gray-700">
                      <img src={imageUrl} alt="Portafolio" loading="lazy" decoding="async" className="w-full h-24 object-cover" />
                      <button
                        type="button"
                        onClick={() => handleRemovePortfolioImage(imageUrl)}
                        className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Services */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-white font-medium">Servicios</label>
                <Button onClick={addService} size="sm" className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
              
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {formData.services.map((service, index) => (
                  <div key={index} className="flex gap-2 items-center bg-gray-800 p-3 rounded-lg">
                    <Input
                      placeholder="Servicio"
                      value={service.name}
                      onChange={(e) => updateService(index, 'name', e.target.value)}
                      className="flex-1 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Precio"
                      value={service.price}
                      onChange={(e) => updateService(index, 'price', parseInt(e.target.value.replace(/\D/g, ''), 10) || 0)}
                      className="w-24 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Min"
                      value={service.duration}
                      onChange={(e) => updateService(index, 'duration', parseInt(e.target.value.replace(/\D/g, ''), 10) || 0)}
                      className="w-16 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    />
                    <Button
                      onClick={() => removeService(index)}
                      size="sm"
                      variant="outline"
                      className="border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveChanges} className="flex-1 bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
              <Button onClick={() => setIsEditing(false)} variant="outline" className="flex-1 text-white border-gray-600">
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reviews Management Modal */}
      <Dialog open={showReviews} onOpenChange={setShowReviews}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Gestión de Reseñas</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {reviews.length > 0 ? (
              reviews.map((review, index) => (
                <div key={review.id || `review-${index}`} data-review-id={review.id}>
                  <ReviewCard
                    review={review}
                    onRespond={(response) => {
                      console.log('Respondiendo a reseña:', review.id, 'con respuesta:', response);
                      respondToReview(review.id, response);
                    }}
                    onPublish={(published) => {
                      console.log('Publicando reseña:', review.id, 'published:', published);
                      publishReview(review.id, published);
                    }}
                    onHide={(hidden) => {
                      console.log('Ocultando reseña:', review.id, 'hidden:', hidden);
                      hideReview(review.id, hidden);
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400">No hay reseñas aún</p>
                <p className="text-gray-500 text-sm">Las reseñas de tus clientes aparecerán aquí</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Review Card Component
const ReviewCard = ({ review, onRespond, onPublish, onHide }) => {
  const [showResponse, setShowResponse] = useState(false);
  const [responseText, setResponseText] = useState('');

  const handleSubmitResponse = () => {
    if (responseText.trim()) {
      onRespond(responseText);
      setResponseText('');
      setShowResponse(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white font-medium">{review.client_name}</span>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${star <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`}
                  />
                ))}
              </div>
              {review.published === false && (
                <Badge variant="outline" className="text-yellow-400 border-yellow-400 text-xs ml-2">
                  No publicada
                </Badge>
              )}
              {review.hidden === true && (
                <Badge variant="outline" className="text-red-400 border-red-400 text-xs ml-2">
                  Ocultada
                </Badge>
              )}
            </div>
            <p className="text-gray-300 text-sm">{review.comment}</p>
            <p className="text-gray-500 text-xs mt-2">
              {formatDateWithTZ(review.created_at)}
            </p>
          </div>
        </div>

        {review.barber_response ? (
          <div className="bg-gray-900 p-3 rounded-lg mt-3">
            <div className="flex items-center gap-2 mb-1">
              <Reply className="w-4 h-4 text-blue-400" />
              <span className="text-blue-300 text-sm font-medium">Tu respuesta:</span>
            </div>
            <p className="text-gray-300 text-sm">{review.barber_response}</p>
          </div>
        ) : (
          <div className="mt-3">
            {showResponse ? (
              <div className="space-y-2">
                <Textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Escribe tu respuesta..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button onClick={handleSubmitResponse} size="sm" className="bg-blue-600 hover:bg-blue-700">
                    Enviar Respuesta
                  </Button>
                  <Button onClick={() => setShowResponse(false)} size="sm" variant="outline" className="text-white border-gray-600">
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={() => setShowResponse(true)} 
                  size="sm" 
                  variant="outline" 
                  className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                >
                  <Reply className="w-4 h-4 mr-1" />
                  Responder
                </Button>
                <Button 
                  onClick={() => onPublish(!(review.published !== false))} 
                  size="sm" 
                  variant="outline" 
                  className={(review.published !== false) ? "text-green-400 border-green-400 hover:bg-green-400 hover:text-white" : "text-yellow-400 border-yellow-400 hover:bg-yellow-400 hover:text-white"}
                >
                  {(review.published !== false) ? (
                    <>
                      <Eye className="w-4 h-4 mr-1" />
                      Publicada
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-1" />
                      Publicar
                    </>
                  )}
                </Button>
                <Button 
                  onClick={() => onHide(!(review.hidden === true))} 
                  size="sm" 
                  variant="outline" 
                  className={(review.hidden === true) ? "text-red-400 border-red-400 hover:bg-red-400 hover:text-white" : "text-gray-400 border-gray-400 hover:bg-gray-400 hover:text-white"}
                >
                  {(review.hidden === true) ? (
                    <>
                      <Eye className="w-4 h-4 mr-1" />
                      Mostrar
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-1" />
                      Ocultar
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* Botones de acción cuando ya hay respuesta */}
        {review.barber_response && (
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button 
              onClick={() => onPublish(!(review.published !== false))} 
              size="sm" 
              variant="outline" 
              className={(review.published !== false) ? "text-green-400 border-green-400 hover:bg-green-400 hover:text-white" : "text-yellow-400 border-yellow-400 hover:bg-yellow-400 hover:text-white"}
            >
              {(review.published !== false) ? (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Publicada
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-1" />
                  Publicar
                </>
              )}
            </Button>
            <Button 
              onClick={() => onHide(!(review.hidden === true))} 
              size="sm" 
              variant="outline" 
              className={(review.hidden === true) ? "text-red-400 border-red-400 hover:bg-red-400 hover:text-white" : "text-gray-400 border-gray-400 hover:bg-gray-400 hover:text-white"}
            >
              {(review.hidden === true) ? (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Mostrar
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-1" />
                  Ocultar
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
